/**
 * Compound Transform — SMARTS-based substructure replacement.
 *
 * Two paths now coexist:
 *
 * 1. **Client-side (preferred)** — when the caller supplies a
 *    `reactionSmarts` (atom-mapped SMARTS like `[c:1][OH]>>[c:1][Cl]`),
 *    `RDKitExport.runReaction` executes it locally via the newer
 *    `@rdkit/rdkit` MinimalLib (`get_rxn` + `JSReaction.run_reactants`).
 *    No network call. Added 2026-05-15 in the
 *    `gibbs-self-hosted-substructure-replace` Path A migration.
 *
 * 2. **Server-side (legacy fallback)** —
 *    `POST /services/medchem/replace-substructure/` (Python RDKit's
 *    `Chem.AllChem.ReplaceSubstructs`). Used when `reactionSmarts` is
 *    absent. Endpoint not yet deployed; `service_unavailable` until
 *    Dan ships.
 *
 * @module gibbs/knowledge/compoundTransform
 */

// Lazy-load RDKitExport inside the function rather than at module top.
// The plugin imports a `.wasm` blob which the production webpack
// bundler handles fine but the Node test loader cannot resolve.
// Deferring keeps the test environment importable even when the WASM
// path isn't reachable.

/**
 * @typedef {Object} ReplaceSubstructureResult
 * @property {string[]} analogs - Product SMILES strings
 * @property {string|null} error - Error code from server (verbatim) or
 *   `'service_unavailable'` if the endpoint can't be reached
 */

/** Default endpoint. Overridable for testing. */
let endpoint = '/services/medchem/replace-substructure/';

/**
 * Override the endpoint URL (test-only).
 * @param {string} url
 */
export function setEndpoint(url) {
    endpoint = url;
}

/** Default cap on returned analogs when caller omits maxResults. */
const DEFAULT_MAX_RESULTS = 3;

/**
 * Apply a SMARTS-driven substructure replacement to a SMILES.
 *
 * When `options.reactionSmarts` is supplied, the replacement runs
 * client-side via `RDKitExport.runReaction` (no network call). When
 * absent, falls back to `POST /services/medchem/replace-substructure/`
 * — which returns `service_unavailable` until Dan's endpoint deploys.
 *
 * @param {string} smiles - Input compound SMILES
 * @param {string} fromPattern - SMARTS pattern to match (server path)
 * @param {string} toFragment - Replacement SMILES fragment (server path)
 * @param {{ maxResults?: number, reactionSmarts?: string }} [options]
 * @returns {Promise<ReplaceSubstructureResult>}
 */
export async function replaceSubstructure(smiles, fromPattern, toFragment, options = {}) {
    const maxResults = options.maxResults ?? DEFAULT_MAX_RESULTS;

    // ── Client-side fast path (gibbs-self-hosted-substructure-replace Path A)
    if (options.reactionSmarts) {
        try {
            // Lazy import — see top-of-file comment.
            const { RDKitExport } = await import('../../plugin/RDKitExport');
            if (RDKitExport.isInitialized && RDKitExport.isInitialized()) {
                const analogs = RDKitExport.runReaction(
                    smiles, options.reactionSmarts, { maxResults }
                );
                return { analogs, error: null };
            }
            // WASM not yet ready; surface a distinct error so the caller
            // can retry or fall back to chat narration.
            return { analogs: [], error: 'rdkit_not_ready' };
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn(
                '[compoundTransform] client-side runReaction error:',
                err?.message
            );
            return { analogs: [], error: 'client_runtime_error' };
        }
    }

    // ── Legacy server-side path
    let response;
    try {
        response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
                smiles, fromPattern, toFragment, maxResults,
            }),
        });
    } catch (err) {
        // Network failure, CORS, etc.
        return { analogs: [], error: 'service_unavailable' };
    }

    if (!response.ok) {
        // 4xx/5xx — typically the endpoint hasn't been deployed yet (404)
        // or the server is having a bad day (5xx). Same degraded response
        // either way; the caller surfaces verbatim.
        return { analogs: [], error: 'service_unavailable' };
    }

    let body;
    try {
        body = await response.json();
    } catch (err) {
        return { analogs: [], error: 'service_unavailable' };
    }

    return {
        analogs: Array.isArray(body.analogs) ? body.analogs : [],
        error: body.error ?? null,
    };
}

/**
 * Heteroaromatic ring SMARTS lookup for the swap-heteroaromatic-ring
 * handler. Each entry pairs a ring name with the SMARTS pattern that
 * matches the ring and the SMILES for the replacement.
 *
 * Source: Meanwell, N.A. A Synopsis of the Properties and Applications
 * of Heteroaromatic Rings in Medicinal Chemistry. Adv. Heterocycl. Chem.
 * 2017, 123, 245–361.
 */
export const HETEROAROMATIC_SWAPS = Object.freeze({
    phenyl: { pattern: 'c1ccccc1', fragment: 'c1ccccc1' },
    pyridine: { pattern: 'c1ccncc1', fragment: 'c1ccncc1' },
    thiophene: { pattern: 'c1ccsc1', fragment: 'c1ccsc1' },
    thiazole: { pattern: 'c1cscn1', fragment: 'c1cscn1' },
    oxazole: { pattern: 'c1cocn1', fragment: 'c1cocn1' },
    pyrazole: { pattern: 'c1cn[nH]c1', fragment: 'c1cn[nH]c1' },
    imidazole: { pattern: 'c1cnc[nH]1', fragment: 'c1cnc[nH]1' },
    furan: { pattern: 'c1ccoc1', fragment: 'c1ccoc1' },
});

/**
 * Lookup a ring-swap recipe.
 * @param {string} fromRing
 * @param {string} toRing
 * @returns {{ fromPattern: string, toFragment: string }|null}
 */
export function getBuiltInRingSwap(fromRing, toRing) {
    const from = HETEROAROMATIC_SWAPS[fromRing];
    const to = HETEROAROMATIC_SWAPS[toRing];
    if (!from || !to) return null;
    return { fromPattern: from.pattern, toFragment: to.fragment };
}
